import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { ChevronLeft, ChevronRight, ChevronDown, LogOut, User, Home, Database, BookOpen, RefreshCw, ShieldCheck, LayoutList } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'

interface MenuItem {
  label: string
  icon?: LucideIcon
  path?: string
  children?: { label: string; icon?: LucideIcon; path: string }[]
}

const menuItems: MenuItem[] = [
  { label: '首页', icon: Home, path: '/' },
  {
    label: '数据对接',
    icon: Database,
    children: [
      { label: '连接管理', icon: Database, path: '/data-integration/connections' },
      { label: '型录管理', icon: BookOpen, path: '/data-integration/catalog' },
      { label: '规范表型录', icon: LayoutList, path: '/data-integration/normalized-catalog' },
      { label: '同步任务', icon: RefreshCw, path: '/data-integration/sync' },
      { label: '数据审计', icon: ShieldCheck, path: '/data-integration/audit' },
    ],
  },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    '数据对接': true,
  })

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('已退出登录')
    } catch {
      toast.error('退出失败')
    }
  }

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }))
  }

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <aside
      className={cn(
        'flex flex-col bg-card border-r border-border transition-all duration-200',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-14 px-4 border-b border-border">
        {!collapsed && (
          <span className="font-semibold text-foreground truncate">
            {import.meta.env.VITE_APP_TITLE || '必竟宜居'}
          </span>
        )}
        <button
          onClick={onToggle}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
          title={collapsed ? '展开' : '收起'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {menuItems.map(item => {
          if (item.path) {
            // Simple link item
            const Icon = item.icon
            return (
              <Link
                key={item.label}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 px-4 py-1.5 text-sm transition-colors',
                  isActive(item.path)
                    ? 'border-l-[2px] border-l-primary font-medium text-foreground'
                    : 'border-l-[2px] border-l-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
                title={collapsed ? item.label : undefined}
              >
                {Icon && <Icon className="h-4 w-4 shrink-0" />}
                {!collapsed && item.label}
              </Link>
            )
          }

          // Group with children
          const isExpanded = expandedGroups[item.label] && !collapsed
          const hasActiveChild = item.children?.some(c => isActive(c.path))

          const GroupIcon = item.icon
          return (
            <div key={item.label}>
              <button
                onClick={() => !collapsed && toggleGroup(item.label)}
                className={cn(
                  'flex items-center gap-3 w-full px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-colors',
                  hasActiveChild
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
                title={collapsed ? item.label : undefined}
              >
                {GroupIcon && <GroupIcon className="h-4 w-4 shrink-0" />}
                {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                {!collapsed && (
                  isExpanded
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
              {isExpanded && item.children && (
                <div>
                  {item.children.map(child => {
                    const ChildIcon = child.icon
                    return (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={cn(
                          'flex items-center gap-3 pl-9 pr-4 py-1.5 text-sm transition-colors',
                          isActive(child.path)
                            ? 'border-l-[2px] border-l-primary font-medium text-foreground'
                            : 'border-l-[2px] border-l-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                        )}
                      >
                        {ChildIcon && <ChildIcon className="h-4 w-4 shrink-0" />}
                        {child.label}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* User & Logout */}
      {user && (
        <div className="border-t border-border px-3 py-3 flex items-center gap-2">
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
          {!collapsed && (
            <span className="flex-1 text-xs text-muted-foreground truncate">
              {user.displayName || user.phone}
            </span>
          )}
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground shrink-0"
            title="退出登录"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      )}
    </aside>
  )
}
